import { CaseRotationStatus } from '#entities';

export type Orientation = 'horizontal' | 'vertical';

export interface CarouselRegisterEvent {
  type: 'carousel-register';
  id: string;
  canMove: boolean;
  variant: Orientation;
}
export interface CarouselUpdateCanMoveEvent {
  type: 'carousel-update-can-move';
  id: string;
  canMove: boolean;
}
export interface CarouselReportEdgeEvent {
  type: 'carousel-report-edge';
  id: string;
  dir: 1 | -1;
}
export interface CarouselTickEvent {
  type: 'carousel-tick';
  dir: 1 | -1;
  targetOrientation?: Orientation;
}
export interface CarouselForceDirectionEvent {
  type: 'carousel-force-direction';
  dir: 1 | -1;
}

export interface UserIdleStartEvent {
  type: 'user-idle-start';
}
export interface UserIdleEndEvent {
  type: 'user-idle-end';
}
export interface CaseLoopStartEvent {
  type: 'case-loop-start';
}
export interface CaseLoopNextEvent {
  type: 'case-loop-next';
}
export interface CaseLoopEndEvent {
  type: 'case-loop-end';
}

export interface CaseRotationUpdateEvent {
  type: 'case-rotation-update';
  value: CaseRotationStatus;
}

export type AppBehaviorEvent =
  | UserIdleStartEvent
  | UserIdleEndEvent
  | CaseLoopStartEvent
  | CaseLoopNextEvent
  | CaseLoopEndEvent
  | CaseRotationUpdateEvent
  | CarouselRegisterEvent
  | CarouselUpdateCanMoveEvent
  | CarouselReportEdgeEvent
  | CarouselTickEvent
  | CarouselForceDirectionEvent;

export type AppBehaviorEventListener = (event: AppBehaviorEvent) => void;

// Map of event types to their corresponding event data
export interface AppBehaviorEventMap {
  'user-idle-start': UserIdleStartEvent;
  'user-idle-end': UserIdleEndEvent;
  'case-loop-start': CaseLoopStartEvent;
  'case-loop-next': CaseLoopNextEvent;
  'case-loop-end': CaseLoopEndEvent;
  'case-rotation-update': CaseRotationUpdateEvent;
  'carousel-register': CarouselRegisterEvent;
  'carousel-update-can-move': CarouselUpdateCanMoveEvent;
  'carousel-report-edge': CarouselReportEdgeEvent;
  'carousel-tick': CarouselTickEvent;
  'carousel-force-direction': CarouselForceDirectionEvent;
}

export class AppBehaviorEventEmitter extends EventTarget {
  // Track active listeners for proper cleanup
  private readonly activeListeners = new Set<() => void>();

  /**
   * Add a listener for a specific transition event type
   * @param type - The event type to listen for
   * @param listener - The listener function that will receive the CustomEvent
   * @param options - Standard AddEventListenerOptions
   * @returns Unsubscribe function
   */
  addListener<T extends keyof AppBehaviorEventMap>(
    type: T,
    listener: (event: CustomEvent<AppBehaviorEventMap[T]>) => void,
    options?: AddEventListenerOptions,
  ): () => void {
    this.addEventListener(type, listener as EventListener, options);

    // Create tracked unsubscribe function
    const unsubscribe = () => {
      this.removeEventListener(type, listener as EventListener, options);
      this.activeListeners.delete(unsubscribe);
    };

    this.activeListeners.add(unsubscribe);
    return unsubscribe;
  }

  /**
   * Emit a transition event
   * @param event - The transition event to emit
   */
  emit(event: AppBehaviorEvent): void {
    const customEvent = new CustomEvent(event.type, {
      detail: event,
      bubbles: false,
      cancelable: false,
    });

    try {
      this.dispatchEvent(customEvent);
    } catch (error) {
      console.error('Error dispatching transition event:', error);
    }
  }

  // ...existing convenience methods remain the same...

  /**
   * Remove all active listeners
   * Follows React cleanup patterns and prevents memory leaks
   */
  clear(): void {
    // Efficiently remove all listeners
    for (const unsubscribe of this.activeListeners) {
      unsubscribe();
    }

    this.activeListeners.clear();
  }

  /**
   * Get the number of active listeners (useful for debugging and monitoring)
   */
  get listenerCount(): number {
    return this.activeListeners.size;
  }

  /**
   * Check if there are any active listeners
   * Useful for performance optimizations
   */
  get hasListeners(): boolean {
    return this.activeListeners.size > 0;
  }

  // Convenience methods for specific event types with proper typing
  addUserIdleStartListener(
    listener: (event: CustomEvent<UserIdleStartEvent>) => void,
    options?: AddEventListenerOptions,
  ): () => void {
    return this.addListener('user-idle-start', listener, options);
  }

  addUserIdleEndListener(
    listener: (event: CustomEvent<UserIdleEndEvent>) => void,
    options?: AddEventListenerOptions,
  ): () => void {
    return this.addListener('user-idle-end', listener, options);
  }

  addCaseLoopStartListener(
    listener: (event: CustomEvent<CaseLoopStartEvent>) => void,
    options?: AddEventListenerOptions,
  ): () => void {
    return this.addListener('case-loop-start', listener, options);
  }

  addCaseLoopNextListener(
    listener: (event: CustomEvent<CaseLoopNextEvent>) => void,
    options?: AddEventListenerOptions,
  ): () => void {
    return this.addListener('case-loop-next', listener, options);
  }

  addCaseLoopEndListener(
    listener: (event: CustomEvent<CaseLoopEndEvent>) => void,
    options?: AddEventListenerOptions,
  ): () => void {
    return this.addListener('case-loop-end', listener, options);
  }

  addCaseRotationUpdateListener(
    listener: (event: CustomEvent<CaseRotationUpdateEvent>) => void,
    options?: AddEventListenerOptions,
  ): () => void {
    return this.addListener('case-rotation-update', listener, options);
  }
}
